import { Component, computed, OnInit, signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { InventoryTypeProxyService } from '../../../proxies/inventory/inventory-type.proxy.service';
import { forkJoin } from 'rxjs';
import { InventoryType } from '../../../models/inventory/inventory-type';
import _ from 'lodash';
import { LocaleHelperService } from '../../../services/localehelperservice.service';
import { EnumService } from '../../../services/enum.service';
import { BreadcrumbService, EnumValues, ModalService, UserLite } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { AssetTypeProxyService } from '../../../proxies/asset/asset-type.proxy.service';
import { AssetType } from '../../../models/asset/assetType';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { ItemMasterRequestService } from '../../../proxies/item/item-master-request.service';
import { DepartmentProxyService } from '../../../proxies/user-master/department-proxy.service';
import { Department } from '../../../models/department/deparment';
import { ItemMasterRequest } from '../../../models/code-creation/code-creation';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { ItemMasterModalComponent } from '../../modals/item-master-modal/item-master-modal.component';
import { DataMasterAdminGroup, DataMasterViewerGroup } from '../../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-code-creation-detail',
  templateUrl: './code-creation-detail.component.html',
  styleUrl: './code-creation-detail.component.scss',
})
export class CodeCreationDetailComponent implements OnInit {
  isEditing = signal(false);
  showOtherInputs = signal(false);
  isLoading = signal(0);
  showPartsAndAssets = signal(false);
  currentUomSelected = signal('');
  currentItemMasterRequest = signal<ItemMasterRequest | null>(null);

  canEdit = computed(() => {
    if (AuthService.hasRole(DataMasterViewerGroup.ItemCodeRequestViewer)) {
      return true;
    }
    if (AuthService.hasRole(DataMasterAdminGroup.ItemMasterAdmin)) {
      return true;
    }
    return false;
  });

  userLite = signal<UserLite | null>(null);
  assetTypeOptions = signal<AssetType[]>([]);
  inventoryTypeOptions = signal<InventoryType[]>([]);
  departmentOptions = signal<Department[]>([]);
  financeOptions = signal<{ label: string; value: number }[]>([]);
  uomsOptions = signal<{ label: string; value: number }[]>([]);
  priorityOptions = signal<{ label: string; value: number }[]>([]);

  uomsEnums: EnumValues;

  //* general information
  inventoryTypeReference = new FormControl<string | null>(null, Validators.required);
  company = new FormControl<string | null>({ value: 'NT', disabled: true });

  //* item details
  itemDescription = new FormControl<string | null>(null, Validators.required);
  manufacturer = new FormControl<string | null>(null, Validators.required);
  mainUom = new FormControl<number | null>(null, Validators.required);
  characteristics = new FormControl<string | null>(null);
  extraComments = new FormControl<string | null>(null);
  assetTypeReference = new FormControl<string | null>(null);
  manufacturerPartIdentifier = new FormControl<string | null>(null);

  //* alternative units of measure
  baseRate = new FormControl<number | null>(null, Validators.min(0));
  uom = new FormControl<number | null>(null);
  conversionRate = new FormControl<number | null>(null, Validators.min(0));
  //* request
  priority = new FormControl<number | null>(null, Validators.required);
  departmentReference = new FormControl<string | null>(null, Validators.required);
  requester = new FormControl<string | null>({
    value: null,
    disabled: true,
  });

  itemMasterCode = new FormControl<string | null>(null);

  isAdminCodeCreation = computed(() => {
    return AuthService.hasRole(DataMasterAdminGroup.ItemMasterAdmin);
  });

  constructor(
    private activatedRoute: ActivatedRoute,
    private businessProxyService: BusinessProxyService,
    private enumService: EnumService,
    private inventoryTypeProxyService: InventoryTypeProxyService,
    public localHelperService: LocaleHelperService,
    private assetTypeProxyService: AssetTypeProxyService,
    private router: Router,
    private itemMasterRequestService: ItemMasterRequestService,
    private departmentProxyService: DepartmentProxyService,
    private userAuthenticationService: UserAuthenticationService,
    private modalService: ModalService,
    private breadcrumbService: BreadcrumbService,
  ) {
    this.userLite.set(this.userAuthenticationService.userLite);

    this.inventoryTypeReference.valueChanges.subscribe((value) => {
      if (value === '6866c3ba245e3504d98d3a4e' || value === '6866c3ce245e3504d98d3a50') {
        this.showPartsAndAssets.set(true);
      } else {
        this.showPartsAndAssets.set(false);
      }
    });

    this.mainUom.valueChanges.subscribe((value) => {
      if (!_.isNil(value)) {
        this.currentUomSelected.set(this.uomsOptions().find((x) => x.value === value)?.label || '');
      }
    });

    const valuesUnitOfMeasure = this.enumToSelectOptions(this.enumService.getEnum('UnitOfMeasure', 'itemEnums').values);
    this.uomsOptions.set(valuesUnitOfMeasure);

    this.uomsEnums = this.enumService.getEnum('UnitOfMeasure', 'itemEnums').values;

    const valuesPriorities = this.enumToSelectOptions(this.enumService.getEnum('PriorityType', 'itemEnums').values);
    this.priorityOptions.set(valuesPriorities);
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['id'] !== 'new') {
        this.isEditing.set(true);
        this.getItemMasterRequest(params['id']);
      } else {
        this.breadcrumbService.setCurrent('Common_New');
        this.requester.setValue(this.userLite()?.username ?? '');
      }
    });
    this.getDataOptions();

    // Disable form controls for viewers
    if (!this.canEdit()) {
      // this.itemMasterCreatedDate.disable();
      this.inventoryTypeReference.disable();
      this.itemDescription.disable();
      this.manufacturer.disable();
      this.mainUom.disable();
      this.characteristics.disable();
      this.extraComments.disable();
      this.assetTypeReference.disable();
      this.manufacturerPartIdentifier.disable();
      this.baseRate.disable();
      this.uom.disable();
      this.conversionRate.disable();
      this.priority.disable();
      this.departmentReference.disable();
    }
    this.disableForms();
  }

  private getItemMasterRequest(id: string) {
    this.isLoading.set(1);
    this.itemMasterRequestService.getById(id).subscribe({
      next: ({ data }) => {
        if (!_.isNil(data)) {
          this.isLoading.set(0);
          this.currentItemMasterRequest.set(data);
          this.populateDataInFormControls(data);
        }
      },
      error: (error) => {
        this.isLoading.set(0);
        console.error(error);
      },
    });
  }

  private getDataOptions() {
    const inventoryType$ = this.inventoryTypeProxyService.getAllInventoryType();
    const assetType$ = this.assetTypeProxyService.getAll();
    const department$ = this.departmentProxyService.getAll();

    this.isLoading.set(1);

    forkJoin([inventoryType$, assetType$, department$]).subscribe(([inventoryType, assetType, department]) => {
      if (!_.isNil(inventoryType.data)) {
        this.inventoryTypeOptions.set(inventoryType.data);
      }

      if (!_.isNil(assetType.data)) {
        this.assetTypeOptions.set(assetType.data);
      }

      if (!_.isNil(department.data)) {
        this.departmentOptions.set(department.data);
      }

      this.isLoading.set(0);
    });
  }

  private populateDataInFormControls(itemMasterRequest: ItemMasterRequest) {
    if (!_.isNil(itemMasterRequest)) {
      this.inventoryTypeReference.setValue(itemMasterRequest.inventoryTypeReference!);
      this.itemDescription.setValue(itemMasterRequest.itemDescription!);
      this.mainUom.setValue(itemMasterRequest.mainUom!);
      this.manufacturer.setValue(itemMasterRequest.manufacturer);
      this.characteristics.setValue(itemMasterRequest.characteristics!);
      this.departmentReference.setValue(itemMasterRequest.departmentReference);
      this.extraComments.setValue(itemMasterRequest.extraComments!);
      this.assetTypeReference.setValue(itemMasterRequest.assetTypeReference!);
      this.manufacturerPartIdentifier.setValue(itemMasterRequest.manufacturerPartIdentifier!);
      this.uom.setValue(itemMasterRequest.supportedUoms![0].uom);
      this.conversionRate.setValue(itemMasterRequest.supportedUoms![0].conversionRate);
      this.priority.setValue(itemMasterRequest.priority!);
      this.requester.setValue(itemMasterRequest.createdBy!);
      this.baseRate.setValue(itemMasterRequest.supportedUoms![0].baseRate);
    }
  }

  private enumToSelectOptions(enumObj: EnumValues): { label: string; value: number }[] {
    return Object.keys(enumObj)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        label: enumObj[Number(key)],
        value: Number(key),
      }));
  }

  get inventoryTypeOptionsForSelect() {
    return this.inventoryTypeOptions().map((item) => {
      return {
        label: this.localHelperService.getLocalizedValue(item.name),
        value: item.id,
      };
    });
  }

  get departmentOptionsForSelect() {
    return this.departmentOptions().map((item) => {
      return {
        label: this.localHelperService.getLocalizedValue(item.name),
        value: item.id,
      };
    });
  }

  private validateField(control: FormControl, message: string): boolean {
    if (_.isNil(control.value)) {
      control.markAsTouched();
      control.setErrors({ required: true });
      GlobalMessageUtil.warn('Warning', message);
      return false;
    }
    return true;
  }

  onSave() {
    const isValid =
      this.validateField(this.inventoryTypeReference, 'Please select inventory type') &&
      this.validateField(this.company, 'Please select Company') &&
      this.validateField(this.departmentReference, "Please select Requester's Area") &&
      this.validateField(this.priority, 'Please select Priority') &&
      this.validateField(this.mainUom, 'Please select Unit of Measure');

    if (this.baseRate.value !== null && this.baseRate.value < 0) {
      GlobalMessageUtil.warn('Warning', 'Base Unit of Measure cannot be negative');
      return;
    }

    if (this.conversionRate.value !== null && this.conversionRate.value < 0) {
      GlobalMessageUtil.warn('Warning', 'Conversion cannot be negative');
      return;
    }

    if (!isValid) {
      return;
    }

    const itemMasterRequest: ItemMasterRequest = {
      itemMasterCreatedDate: _.isNil(this.currentItemMasterRequest()) ? undefined : this.currentItemMasterRequest()!.itemMasterCreatedDate,
      itemMasterCode: _.isNil(this.currentItemMasterRequest()) ? undefined : this.currentItemMasterRequest()!.itemMasterCode,
      itemDescription: this.itemDescription.value ?? '',
      mainUom: this.mainUom.value ?? undefined,
      supportedUoms: [
        {
          uom: this.uom.value ?? 0,
          conversionRate: this.conversionRate.value ?? 0,
          baseRate: this.baseRate.value ?? 0,
        },
      ],
      manufacturer: this.manufacturer.value ?? '',
      characteristics: this.characteristics.value ?? '',
      departmentReference: this.departmentReference.value ?? '',
      extraComments: this.extraComments.value ?? '',
      assetTypeReference: this.showPartsAndAssets() ? (this.assetTypeReference.value ?? '') : undefined,
      manufacturerPartIdentifier: this.showPartsAndAssets() ? (this.manufacturerPartIdentifier.value ?? '') : undefined,
      priority: this.priority.value ?? undefined,
      inventoryTypeReference: this.inventoryTypeReference.value ?? '',
      createdBy: this.requester.value ?? '',
    };
    this.isLoading.set(1);

    if (!this.isEditing()) {
      this.itemMasterRequestService.create(itemMasterRequest).subscribe(({ data }) => {
        if (!_.isNil(data)) {
          this.isLoading.set(0);
          this.currentItemMasterRequest.set(data);
          GlobalMessageUtil.success('Success Created', 'Successfully created item master request');
          this.onCancel();
        }
      });
    } else {
      const editData = {
        id: this.currentItemMasterRequest()!.id,
        ...itemMasterRequest,
      };
      this.itemMasterRequestService.update(editData).subscribe(({ data }) => {
        if (!_.isNil(data)) {
          this.isLoading.set(0);
          GlobalMessageUtil.success('Success Updated', 'Successfully updated item master request');
          this.onCancel();
        }
      });
    }
  }

  onCancel() {
    this.resetFormControls();
    this.router.navigate(['/code-creation-requests']);
  }

  resetFormControls() {
    this.inventoryTypeReference.setValue(null);
    this.itemDescription.setValue(null);
    this.mainUom.setValue(null);
    this.manufacturer.setValue(null);
    this.characteristics.setValue(null);
    this.departmentReference.setValue(null);
    this.extraComments.setValue(null);
    this.assetTypeReference.setValue(null);
    this.manufacturerPartIdentifier.setValue(null);
    this.priority.setValue(null);
  }

  openModalCreateItemMasterRequest() {
    const requestData = this.currentItemMasterRequest();

    if (!requestData) {
      GlobalMessageUtil.error('Error', 'Request data not found');
      return;
    }

    if (requestData.itemMasterCode) {
      GlobalMessageUtil.warn('Warning', `Item Master ${requestData.itemMasterCode} already exists for this request`);
      return;
    }

    const prefilledItemMaster: Record<string, unknown> = {
      // Direct mappings
      inventoryTypeReference: requestData.inventoryTypeReference,
      unitOfMeasure: requestData.mainUom,

      // Convert single string to bilingual format
      itemDescription: {
        en: requestData.itemDescription || '',
        es: '',
        missingTranslations: ['es'],
      },

      // Use description as display name
      itemDisplayName: {
        en: requestData.itemDescription || '',
        es: '',
        missingTranslations: ['es'],
      },

      // Use characteristics as long name
      itemLongDisplayName: {
        en: requestData.characteristics || '',
        es: '',
        missingTranslations: ['es'],
      },

      // Default/required fields that user must fill in modal
      status: 0,
      itemCategoryCode: '',
      itemLowestSubCategoryCode: '',
      itemClassificationCode: '',
    };

    // Open modal with prefilled data
    this.modalService
      .open(
        ItemMasterModalComponent,
        { modalClass: 'modal_middle' },
        {
          itemMaster: prefilledItemMaster, // Pass the built object
          canEdit: true,
          uoms: this.uomsEnums,
          itemRequestReference: requestData.id, // Pass request ID
        },
      )
      .subscribe((result) => {
        console.log('this is result of modal', result);
      });
  }
  private disableForms() {
    if (!this.canEdit()) {
      // general information
      this.inventoryTypeReference.disable();
      // item details
      this.itemDescription.disable();
      this.manufacturer.disable();
      this.mainUom.disable();
      this.characteristics.disable();
      this.extraComments.disable();
      this.assetTypeReference.disable();
      this.manufacturerPartIdentifier.disable();
      // alternative units of measure
      this.baseRate.disable();
      this.uom.disable();
      this.conversionRate.disable();
      // request
      this.priority.disable();
      this.departmentReference.disable();
      this.requester.disable();
    }
  }
}
